import type { Settings } from '../../app/persistence';
import { ALL_ACHIEVEMENTS } from '../../game/achievements';
import { TOPPING_INFO } from '../../game/lingo';
import { TOPPINGS } from '../../game/types';
import { dottieSprite } from '../../render/characters';
import { paintSpriteInto } from '../../render/sprite';
import { h, onPress } from '../dom';
import { ICONS, iconImg } from '../iconImages';

/* Dialogs layered over the title screen or the paused game. */

export class Modal {
  readonly el: HTMLElement;
  private readonly body: HTMLElement;
  private readonly heading: HTMLElement;
  private onClose: (() => void) | null = null;

  constructor() {
    this.heading = h('h2', { class: 'modal__title', id: 'modal-title' });
    this.body = h('div', { class: 'modal__body' });
    const close = h(
      'button',
      { class: 'modal__close', type: 'button', 'aria-label': 'Close' },
      '✕',
    );
    onPress(close, () => this.close());
    this.el = h(
      'div',
      {
        class: 'modal',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-labelledby': 'modal-title',
        hidden: true,
      },
      h(
        'div',
        { class: 'modal__card' },
        h('header', { class: 'modal__head' }, this.heading, close),
        this.body,
      ),
    );
    this.el.addEventListener('pointerdown', (e) => {
      if (e.target === this.el) this.close();
    });
  }

  get isOpen(): boolean {
    return !this.el.hidden;
  }

  open(title: string, content: HTMLElement, onClose?: () => void): void {
    this.heading.textContent = title;
    this.body.replaceChildren(content);
    this.onClose = onClose ?? null;
    this.el.hidden = false;
    this.el.querySelector<HTMLElement>('.modal__close')?.focus();
  }

  close(): void {
    if (this.el.hidden) return;
    this.el.hidden = true;
    const cb = this.onClose;
    this.onClose = null;
    cb?.();
  }
}

export function howToContent(): HTMLElement {
  const lingo = h(
    'div',
    { class: 'lingo' },
    ...TOPPINGS.map((t) =>
      h(
        'div',
        { class: 'lingo__row' },
        iconImg(ICONS.topping(t), 'lingo__icon'),
        h('b', null, TOPPING_INFO[t].lingo),
        h('span', null, TOPPING_INFO[t].ingredient),
      ),
    ),
  );
  return h(
    'div',
    { class: 'howto' },
    h('h3', null, 'The job'),
    h(
      'p',
      null,
      'Work the graveyard shift, 10 PM to 6 AM. Every ticket you serve earns tips. Keep the ',
      h('b', null, 'Waffle House Index'),
      ' green — walkouts, burnt food and flying chairs drag it down. Yellow means a limited menu. Red means the Waffle House closes, and FEMA gets involved.',
    ),
    h('h3', null, 'The lingo'),
    h(
      'p',
      null,
      'Every hash brown is ',
      h('b', null, 'SCATTERED'),
      ' on the grill. ',
      h('b', null, 'WELL'),
      ' means extra crispy. ',
      h('b', null, 'ALL THE WAY'),
      ' means all eight toppings:',
    ),
    lingo,
    h('h3', null, 'The line'),
    h(
      'ol',
      null,
      h('li', null, 'Tap a ticket to work on it (or press 1–5).'),
      h(
        'li',
        null,
        'Tap ',
        h('b', null, 'DROP'),
        ' — or just tap a topping — to scatter hash browns on the grill.',
      ),
      h('li', null, 'Add the toppings the ticket calls for. Tap one again to scrape it off.'),
      h(
        'li',
        null,
        'Tap the hash browns when they’re ',
        h('b', null, 'GOLDEN'),
        ' (',
        h('b', null, 'CRISPY'),
        ' if the ticket says WELL). Hold to toss.',
      ),
      h(
        'li',
        null,
        'Waffles: pick the batter, wait for the ',
        h('b', null, 'DING'),
        ', tap the iron. Eggs and bacon cook themselves.',
      ),
    ),
    h('h3', null, 'The chaos'),
    h(
      'ul',
      null,
      h('li', null, '☕ Tap a customer with an empty mug to refill it. Coffee = tips.'),
      h(
        'li',
        null,
        '🪑 When a chair flies, tap (or SPACE) when the ring closes on it. Perfect timing is extra.',
      ),
      h('li', null, '😐 Orders served during a brawl pay double. Stay unbothered.'),
      h(
        'li',
        null,
        '🌀 Hurricanes cut the power. The waffle irons are electric. The grill is gas. Keep cooking.',
      ),
      h(
        'li',
        null,
        '👁 Something happens at 3:33 AM if the Index is high enough. It wants it ALL THE WAY.',
      ),
    ),
    h('h3', null, 'Between shifts'),
    h(
      'p',
      null,
      'The 3AM crowd won’t leave on its own. Before every new shift, three rowdy customers need ',
      h('b', null, 'calming down'),
      ' — fighting-game style. Their bar is RAGE, not health: every hit calms them a little.',
    ),
    h(
      'ul',
      null,
      h(
        'li',
        null,
        '👊 ',
        h('b', null, 'JAB'),
        ' (J) — three in a row finishes with a spatula smack.',
      ),
      h('li', null, '🥔 ', h('b', null, 'HASH BROWN'), ' (K) — throw one from range.'),
      h(
        'li',
        null,
        '🛡 ',
        h('b', null, 'BLOCK'),
        ' (hold L) when they wind up and flash red. Block right before the hit to PARRY — you can even catch a thrown chair.',
      ),
      h('li', null, '💥 Fill the meter and go ', h('b', null, 'ALL THE WAY'), ' (SPACE).'),
      h(
        'li',
        null,
        '💛 Every customer you calm makes the next shift 8% more patient — and they come back as big tippers.',
      ),
    ),
    h('h3', null, 'Keyboard'),
    h(
      'p',
      { class: 'keys' },
      h('kbd', null, '1–5'),
      ' tickets · ',
      h('kbd', null, 'Q'),
      ' drop · ',
      h('kbd', null, 'A S D F G H J K'),
      ' toppings · ',
      h('kbd', null, 'W E'),
      ' waffles · ',
      h('kbd', null, 'R T'),
      ' eggs · ',
      h('kbd', null, 'Y'),
      ' bacon · ',
      h('kbd', null, 'Z X C V'),
      ' grill · ',
      h('kbd', null, 'B N'),
      ' irons · ',
      h('kbd', null, 'SPACE'),
      ' plate / catch · ',
      h('kbd', null, 'O'),
      ' coffee · ',
      h('kbd', null, 'Shift'),
      '+key toss · ',
      h('kbd', null, 'Esc'),
      ' pause · ',
      h('kbd', null, 'M'),
      ' mute',
    ),
  );
}

export function achievementsContent(unlocked: ReadonlySet<string>): HTMLElement {
  const count = ALL_ACHIEVEMENTS.filter((a) => unlocked.has(a.id)).length;
  return h(
    'div',
    { class: 'achievements' },
    h('p', { class: 'achievements__count' }, `${count} / ${ALL_ACHIEVEMENTS.length} unlocked`),
    h(
      'div',
      { class: 'achievements__grid' },
      ...ALL_ACHIEVEMENTS.map((a) => {
        const got = unlocked.has(a.id);
        const hidden = a.secret && !got;
        return h(
          'div',
          { class: `badge ${got ? 'is-got' : ''}` },
          h('span', { class: 'badge__emoji', 'aria-hidden': 'true' }, hidden ? '❓' : a.emoji),
          h('b', null, hidden ? '???' : a.title),
          h('span', null, hidden ? 'A secret. It’s 3 AM. Anything can happen.' : a.description),
        );
      }),
    ),
  );
}

export function settingsContent(
  settings: Settings,
  onChange: (s: Settings) => void,
  voiceSupported: boolean,
): HTMLElement {
  const row = (key: keyof Settings, label: string, hint: string, disabled = false): HTMLElement => {
    const input = h('input', { type: 'checkbox', id: `set-${key}`, disabled });
    input.checked = settings[key] === true;
    input.addEventListener('change', () => onChange({ ...settings, [key]: input.checked }));
    return h(
      'label',
      { class: 'setting', for: `set-${key}` },
      input,
      h('span', null, h('b', null, label), h('small', null, hint)),
    );
  };
  return h(
    'div',
    { class: 'settings' },
    row('sfx', 'Sound effects', 'Sizzles, bells, crashes.'),
    row('music', 'Jukebox', 'Original chiptunes. Tap the jukebox in-game to change the song.'),
    row(
      'voice',
      'Order callouts',
      voiceSupported
        ? 'Dottie hollers every ticket out loud (speech synthesis).'
        : 'Not supported in this browser.',
      !voiceSupported,
    ),
    row('reducedMotion', 'Reduced motion', 'No screen shake, flashes or lightning.'),
    row('keyHints', 'Keyboard hints', 'Show hotkeys on the buttons.'),
  );
}

export interface IntroResult {
  el: HTMLElement;
  start(): void;
}

const FULL_INTRO = [
  'Welcome to the graveyard shift, baby. Ten PM to six AM.',
  'Keys? We don’t have keys. The doors don’t lock. We never close.',
  'I call the tickets. You drop the hash browns. The buttons tell you what the lingo means.',
  'Keep that Waffle House Index GREEN. FEMA really does watch it.',
  'And if a chair comes flyin’… catch it.',
];

const SHORT_INTRO = ['Doors don’t lock. Chairs fly. Keep it green. Let’s cook.'];

export function introScreen(full: boolean, onDone: () => void): IntroResult {
  const lines = full ? FULL_INTRO : SHORT_INTRO;
  let i = 0;
  const face = h('canvas', { class: 'intro__face', width: 20, height: 30 });
  paintSpriteInto(face, dottieSprite('talk'), 4);
  const line = h('p', { class: 'intro__line', 'aria-live': 'polite' });
  const next = h(
    'button',
    { class: 'cta cta--small', type: 'button' },
    full ? 'NEXT ▸' : 'LET’S COOK ▸',
  );
  const skip = h('button', { class: 'btn-ghost', type: 'button' }, 'skip');
  const el = h(
    'section',
    { class: 'screen screen--intro' },
    h(
      'div',
      { class: 'intro__card' },
      face,
      h(
        'div',
        { class: 'intro__name' },
        'MISS DOTTIE',
        h('small', null, 'SHIFT LEAD · 31 YEARS · HAS SEEN THINGS'),
      ),
      line,
      h('div', { class: 'intro__actions' }, skip, next),
    ),
  );
  const show = (): void => {
    line.textContent = `“${lines[i] ?? ''}”`;
    line.classList.remove('is-in');
    void line.offsetWidth;
    line.classList.add('is-in');
    next.textContent = i >= lines.length - 1 ? 'LET’S COOK ▸' : 'NEXT ▸';
  };
  const advance = (): void => {
    i++;
    if (i >= lines.length) onDone();
    else show();
  };
  onPress(next, advance);
  onPress(skip, onDone);
  return {
    el,
    start: () => {
      i = 0;
      show();
      next.focus();
    },
  };
}
